import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Join Linux to the AD you already run',
    description:
      'Domain-join Linux hosts into your existing Active Directory. AD users sign in with AD credentials: no parallel identity silo to build or maintain.',
    bullets: [
      'Domain join for Linux hosts',
      'AD users sign in with AD credentials',
      'No parallel identity silo',
      'Agentless first: works with the native SSSD stack',
    ],
  },
  {
    title: 'Access policy by AD group',
    description:
      'Host access follows the groups your identity team already manages. Change the group, and Linux access changes with it.',
    bullets: [
      'Map AD groups to host access',
      'Group changes propagate to Linux automatically',
      'Leavers lose Linux access when AD disables them',
      'No more stale local accounts',
    ],
  },
  {
    title: 'Health you can see',
    description:
      'Domain joins break quietly, until someone can’t log in. Monitor SSSD and join health centrally, and catch drift before it becomes an outage.',
    bullets: [
      'SSSD and domain-join health monitored centrally',
      'Broken joins surface before users call',
      'Drift and misconfiguration flagged',
      'One view across the whole Linux estate',
    ],
  },
  {
    title: 'The report auditors actually ask for',
    description:
      '“Who logged into which Linux host with which identity?” With AD bridging, the answer is a report, not an investigation.',
    bullets: [
      'Who logged into which Linux host, with which AD identity',
      'Session records tied to central identities',
      'SSH host-key fingerprint recording (opt-in) against man-in-the-middle surprises',
      'Evidence exportable for audits',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Discover and join',
    description:
      'Find the Linux hosts living outside AD and join them to the domain: agentless, over the native SSSD stack, in minutes.',
  },
  {
    title: 'Map groups to access',
    description:
      'Define which AD groups reach which hosts; joiners, movers, and leavers are handled by the directory you already run.',
  },
  {
    title: 'Prove it',
    description:
      'Watch join health centrally and hand auditors a login trail tied to real AD identities, not local accounts.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Linux identity agent',
    description:
      'Passwordless login and sudo least privilege for the same hosts.',
    href: '/pam/linux',
  },
  {
    label: 'Gateway',
    description:
      'Broker, record, and control the sessions into those hosts.',
    href: '/pam/gateway',
  },
  {
    label: 'Audit & compliance',
    description:
      'How the login trail becomes audit evidence.',
    href: '/solutions/audit-compliance',
  },
]

export default function AdBridgingPage() {
  return (
    <Layout>
      <SolutionMeta
        title="AD bridging: bring Linux into Active Directory"
        description="Join Linux hosts to the Active Directory you already run: agentless, over the native SSSD stack. AD-group access policy, centrally monitored join health, and a login trail that answers who accessed which Linux host with which AD identity."
        path="/pam/ad-bridging"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monopam · AD Bridging"
          title={
            <>
              Your AD governs Windows.
              <br />
              Let it govern Linux too.
            </>
          }
          body="Your identity team runs Active Directory for Windows, while Linux servers live outside it with local accounts. When auditors ask who logged into which Linux box with which identity, nobody can answer. AD bridging joins Linux to the directory you already run, agentless in its first form, so AD identities, groups, and policies govern Linux access too."
          badges={[
            'Agentless, native SSSD',
            'AD-group access policy',
            'Audit-ready login trail',
          ]}
        />
        <SolutionPillars
          title={<>One directory. Every host.</>}
          body="Extend the identities, groups, and policies you already manage in Active Directory to the Linux estate that has been living outside them."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>From local accounts to directory-governed access.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
